import React from "react";
import { 
  Box, 
  Divider,  
  Stack, 
  Typography 
} from "@mui/material";
import {
  FormProvider,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import MyLoadingButton from "../../../../components/buttons/MyLoadingButton";
import { MasterThemeButton } from "../../../../components/buttons/ThemeButton";
import { ChangeLangMaster } from "../../../../components/LanguageSwitcher";
import CustomAccordion from "../../../../components/display/CustomAccordion";
import UserHint from "../../../../components/display/UserHint";
import { EDIT_USER_PREFERENCES } from "../../../../enums/ApiPaths";
import ChangeCalendarView from "../../../profile/component/chips/ChangeCalendarView";
import { ChangeClinicMaster } from "../../../profile/component/chips/ChangeClinic";
import usePutClinic from "../../../profile/hooks/usePutClinic";
import usePutProfile from "../../../profile/hooks/usePutProfile";
import { CheckboxForm, userPreferencesCheckboxes } from "../../../profile/component/chips/Settings";


export default function UserPreferences({ preferance }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const put = usePutProfile(EDIT_USER_PREFERENCES + id);
  const putClinic = usePutClinic(id);

  const methods = useForm({
    defaultValues: {
      language: preferance.defaultLanguage,
      theme: preferance.defaultTheme,
      defaultCalendarView: preferance.defaultCalendarView,
      sendEmailNotifications: preferance.defaultEmailSending,
      sendAppointmentEmails: preferance.sendAppointmentEmails,
      sendAppointmentEmailsWhenUpdated: preferance.sendAppointmentEmailsWhenUpdated,
      sendInboxEmails: preferance.sendInboxEmails,
      disableAllEmails: preferance.disableAllEmails
    },
  });

  const themeBody = {
    title: "defaultTheme",
    component: (
      <Box>
        <MasterThemeButton defaultTheme={preferance?.defaultTheme} />
      </Box>
    ),
    showTitle: true,
  };

  const langBody = {
    title: "defaultLanguage",
    component: (
      <ChangeLangMaster defaultLanguage={preferance?.defaultLanguage} />
    ),
    showTitle: true,
  };

  const calendarView = {
    title: "defaultCalendarView",
    component: (
      <ChangeCalendarView
        defaultCalendarView={preferance.defaultCalendarView}
      />
    ),
    showTitle: false,
  };

  const changeClinic = {
    title: "ChangeClinic",
    component: <ChangeClinicMaster currentClinic={preferance.clinic} />,
    showTitle: false,
  };

  const sendEmailNotifications = {
    title: "WantToReceiveEmailNotificationsAboutAppointments",
    component: (
      <CheckboxForm 
        checked={preferance.defaultEmailSending} 
        name="sendEmailNotifications" 
        label="WantToReceiveEmailNotificationsAboutAppointments"
      />
    ),
    showTitle: true,
  };

  const sendAppointmentEmails = {
    title: "sendAppointmentEmails",
    component: (
      <CheckboxForm 
        checked={preferance.sendAppointmentEmails} 
        name="sendAppointmentEmails" 
        label="sendAppointmentEmails"
      />
    ),
    showTitle: true,
    info: <UserHint iconOnly hint={"SendAppointmentEmailsUserHint"}/>
  };

  const sendAppointmentEmailsWhenUpdated = {
    title: "sendAppointmentEmailsWhenUpdated",
    component: (
      <CheckboxForm 
        checked={preferance.sendAppointmentEmailsWhenUpdated} 
        name="sendAppointmentEmailsWhenUpdated" 
        label="sendAppointmentEmailsWhenUpdated"
      />
    ),
    showTitle: true,
    info: <UserHint iconOnly hint={"SendAppointmentEmailsWhenUpdatedUserHint"}/>
  };

  const sendInboxEmails = {
      title: userPreferencesCheckboxes.sendInboxEmails,
      component: (
        <FormProvider {...methods}>
          <CheckboxForm 
            checked={preferance.sendInboxEmails} 
            name={userPreferencesCheckboxes.sendInboxEmails} 
            label={userPreferencesCheckboxes.sendInboxEmails}  
            key={userPreferencesCheckboxes.sendInboxEmails} 
          />
        </FormProvider>
      ),
      showTitle: true,
      info: <UserHint iconOnly hint={"SendInboxEmailsUserHint"}/>
  }
  
  const disableAllEmails = {
      title: userPreferencesCheckboxes.disableAllEmails,
      component: (
        <FormProvider {...methods}>
          <CheckboxForm 
            checked={preferance.disableAllEmails} 
            name={userPreferencesCheckboxes.disableAllEmails} 
            label={userPreferencesCheckboxes.disableAllEmails} 
            key={userPreferencesCheckboxes.disableAllEmails} />
        </FormProvider>
      ),
      showTitle: true,
      info: <UserHint iconOnly hint={"disableAllEmailsUserHint"}/>
  }

  // Group settings into logical sections
  const settingsGroups = [
    {
      title: "Preferences",
      items: [
        themeBody,
        langBody,
        calendarView,
      ],
      expanded: true
    },
    {
      title: "EmailsAndInbox",
      items: [
        ...(preferance.isActive ? [] : [sendEmailNotifications]),
        sendAppointmentEmails,
        sendAppointmentEmailsWhenUpdated,
        sendInboxEmails,
        disableAllEmails
      ],
      expanded: true
    },
    {
      title: "Clinic",
      items: [changeClinic],
      expanded: true
    }
  ];

  const submitData = async (e) => {
    const data = {
      theme: e?.theme ?? preferance?.defaultTheme,
      language: e.language ?? preferance?.defaultLanguage,
      calendarView: e.defaultCalendarView ? e.defaultCalendarView : "agenda",
      sendAppointmentEmails: e.sendAppointmentEmails,
      sendAppointmentEmailsWhenUpdated: e.sendAppointmentEmailsWhenUpdated,
      sendEmailNotifications: e.sendEmailNotifications,
      disableAllEmails: e.disableAllEmails,
      sendInboxEmails: e.sendInboxEmails
    };

    await put.submitCallback(data);

    if (e?.clinic !== null && preferance?.clinic?.id !== e?.clinic?.id) {
      await putClinic.submitCallback(e.clinic.id);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitData)}>
        <Stack spacing={3}>
          {settingsGroups.map((group, groupIndex) => (
            <CustomAccordion 
              key={groupIndex} 
              title={t(group.title)} 
              defaultExpanded={group.expanded}
            >
              <Stack spacing={2}>
                {group.items.map((item, itemIndex) => (
                  <React.Fragment key={itemIndex}>
                    <Stack spacing={1}>
                      {item.showTitle && (
                        <Typography variant="body2" color={item.textColor || "text.secondary"}>
                          {t(item.title)}
                        </Typography>
                      )}
                      {item?.info ? (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          {item.component}
                          {item.info}
                        </Stack>
                      ) : (
                        item.component
                      )}
                    </Stack>
                    {itemIndex < group.items.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            </CustomAccordion>
          ))}

          <Box>
            <MyLoadingButton message={t("SaveChanges")} />
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
}
